import React, { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'

import { ExpandLess, ExpandMore } from '@mui/icons-material';

import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Typography
} from '../../../../components/v2/styled';

import { User } from '../../../../models/User';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Per-link rendering config: navigate target.
// Kept here (UI concern) rather than in the helper, which only owns data/logic.
const LINK_RENDER: Record<string, {
  labelKey: string;
  navigate: (object: User) => string;
}> = {
  appointments: {
    labelKey: 'coach.general.actions.to_appointments',
    navigate: (object) => `/user/${object.id}/appointments`,
  },
  diary: {
    labelKey: 'coach.general.actions.to_diary',
    navigate: (object) => `/user/${object.id}/diary`,
  },
  courses: {
    labelKey: 'coach.general.actions.to_courses',
    navigate: (object) => `/user/${object.id}/courses`,
  },
  mollie_links: {
    labelKey: 'coach.general.actions.to_mollie_links',
    navigate: (object) => `/user/${object.id}/payments`,
  },
  questionnaires: {
    labelKey: 'coach.general.actions.to_questionnaires',
    navigate: (object) => `/user/${object.id}/questionnaires`,
  },
};

type Props = {
  object: User;
}

export const CoachUserWidgetsGotoLinks: FunctionComponent<Props> = ({
  object
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const navigate = useNavigate();
  const {t} = useTranslation(['translations']);
  const [showAll, setShowAll] = useState<boolean>(false);

  // Goto-links settings are a coach preference, stored alongside the widget
  // layout on login.settings.userdetails_widgets.settings. `goto_links` is the
  // show/hide map; `goto_links_order` (optional) overrides the default order.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const visibility = widgetSettings?.[HelpersClientWidgets.gotoLinksValueKey];
  const order = widgetSettings?.[HelpersClientWidgets.gotoLinksOrderKey];

  const items = HelpersClientWidgets.getOrderedGotoLinks(order, visibility, currentPractice);
  const renderable = items.filter((item) => !!LINK_RENDER[item.key]);
  const hasHidden = renderable.some((i) => i.state === 'inactive');

  return <Paper sx={{padding: 2, marginTop: 1}}>
    <Grid container spacing={1} sx={{position: 'relative'}}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.goto_links")}</Typography>
        <Divider />
      </Grid>
      {renderable.map((item) => {
        if (!(item.state === 'active' || (showAll && item.state === 'inactive'))) return null;
        const render = LINK_RENDER[item.key];
        const { Icon } = item;
        return <Grid item xs={12} key={item.key} sx={{textAlign: 'center'}}>
          <Button
            label={t(render.labelKey)}
            id={`goto_${item.key}`}
            fullwidth
            startIcon={<Icon />}
            onClick={() => {
              navigate(render.navigate(object), {state: {user_id: object.id}});
            }}
            sx={{marginTop: 1}}
          />
        </Grid>;
      })}

      {hasHidden && <Grid item xs={12} sx={{textAlign: 'center', marginTop: 1}}>
        <Button
          label={t(showAll
            ? 'coach.personal_settings.userdetail_widgets.goto_links.show_less'
            : 'coach.personal_settings.userdetail_widgets.goto_links.show_all')}
          id='goto_links_show_all'
          text
          sx={{marginTop: 0}}
          endIcon={showAll ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowAll(!showAll)}
        />
      </Grid>}
    </Grid>
  </Paper>;
}
